import { Alert } from "@caspeco/casper-ui-library";
import { useAvailableTimes } from "api/api-hooks/use-available-times";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import AlertSkeleton from "components/skeletons/alert-skeleton";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useSelectedRule, useSelectedRules } from "hooks/use-selected-rule";
import { useTranslation } from "react-i18next";
import { getSearchParams } from "search-params";
import { CurrencyDisplay } from "translations/currency";
import { getPaymentCalculationTotal, getPaymentUnitTranslation } from "utils/payment-utils";
const PrepaymentAlert = () => {
    const { t } = useTranslation();
    const rules = useSelectedRules();
    const selectedRule = useSelectedRule();

    const state = useBookingStateContext();
    const { data: webBooking } = useWebBooking();
    const reservation = useWebBooking();

    const availableTimes = useAvailableTimes(state.date);

    if (availableTimes.isPending && !webBooking?.webBookingRules) return <AlertSkeleton />;

    // Every branch below quotes an amount, so without the booking there is nothing truthful to say.
    if (!reservation.data) return <AlertSkeleton />;

    const chargeTotal = reservation.data.chargeShouldPayAmount;

    /**
     * An experience is sold as one package, so the guest is given the total to prepay and nothing
     * about how it divides per guest, per resource or per article. This covers a plain activity
     * booked from the experience view too, which is sold the same way.
     */
    if (getSearchParams().isExperienceViewMode) {
        return (
            <Alert type="info" iconVerticalAlign="flex-start">
                {t("prePaymentPackageInfoAlert", {
                    totalValue: chargeTotal,
                    display: CurrencyDisplay.Symbol,
                })}
            </Alert>
        );
    }

    const guests = state.guests ? state.guests : (webBooking?.guests ?? 0);

    const paymentTotalFromRule = getPaymentCalculationTotal(
        selectedRule ?? null,
        guests,
        reservation.data?.articles?.filter(
            (art) => art.isTable && art.ruleId === selectedRule?.id
        ) ?? []
    );

    /**
     * If more than one rule is selected we show a generic alert
     * or if the rule’s payment total is 0, indicating that the payment is derived from the menu cost
     */
    if (rules.length > 1 || paymentTotalFromRule === 0) {
        return (
            <Alert type="info" iconVerticalAlign="flex-start">
                {t("prePaymentMultiInfoAlert", {
                    totalValue: chargeTotal,
                })}
            </Alert>
        );
    }

    /**
     * `paymentTotalFromRule` only covers the resource, so it falls short of the charge whenever
     * priced articles are part of the prepayment. Naming the total first keeps this alert in step
     * with the summary and the pay button, and the resource amount stays as the explanation of it.
     *
     * The two sides are reached differently — the rule total is multiplied here, the charge comes
     * from the backend — so a rule such as 33.30 per guest leaves float residue behind. Anything
     * under a minor unit is that residue rather than an article.
     */
    const articlesValue = chargeTotal - paymentTotalFromRule;

    if (articlesValue >= 0.01) {
        return (
            <Alert type="info" iconVerticalAlign="flex-start">
                {t("prePaymentInfoAlertWithArticles", {
                    totalValue: chargeTotal,
                    resourceValue: paymentTotalFromRule,
                    articlesValue,
                    unitAmount: selectedRule?.paymentAmount,
                    display: CurrencyDisplay.Symbol,
                    unit: selectedRule ? getPaymentUnitTranslation(selectedRule) : "",
                })}
            </Alert>
        );
    }

    return (
        <Alert type="info" iconVerticalAlign="flex-start">
            {t("prePaymentInfoAlert", {
                totalValue: paymentTotalFromRule,
                unitAmount: selectedRule?.paymentAmount,
                display: CurrencyDisplay.Symbol,
                unit: selectedRule ? getPaymentUnitTranslation(selectedRule) : "",
            })}
        </Alert>
    );
};

export default PrepaymentAlert;
